import { roleSpecificNavItems } from "./navItems";
import { faArrowRightFromBracket } from "@fortawesome/free-solid-svg-icons";
import { useContext } from "react";
import { UserContext } from "@/components/providers/UserContext/UserContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getUser } from "@/services/user-services";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";

import { DesktopNav } from "./DesktopNav";
import { MobileNav } from "./MobileNav";

export function Navigation() {
  const { user, viewAsId, setViewAsId } = useContext(UserContext);
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: clientUser } = useQuery({
    queryKey: ["user", viewAsId],
    queryFn: () => getUser(viewAsId!),
    enabled: !!viewAsId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const handleViewAsChange = (clientId: number | null) => {
    if (setViewAsId) {
      if (clientId) {
        localStorage.setItem("viewAsUserId", String(clientId));
      } else {
        localStorage.removeItem("viewAsUserId");
      }

      setViewAsId(clientId);
      // Invalidate all queries to refetch data for the new user context
      queryClient.invalidateQueries();
      navigate("/");
    }
  };

  const userRole = (user?.role || "client") as keyof typeof roleSpecificNavItems;
  const mainNavItems = roleSpecificNavItems[userRole]?.filter((item) => item.level === 1) || [];
  const secondaryNavItems = roleSpecificNavItems[userRole]?.filter((item) => item.level === 2) || [];
  const logoutNavItem = { path: "logout", icon: faArrowRightFromBracket, label: "Logout", level: 2 as const };
  const allNavItems = [...mainNavItems, ...secondaryNavItems, logoutNavItem];

  const handleLogoutClick = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
      <DesktopNav
        user={user}
        clientUser={clientUser}
        viewAsId={viewAsId ?? null}
        allNavItems={allNavItems}
        handleViewAsChange={handleViewAsChange}
        handleLogoutClick={handleLogoutClick}
      />
      <MobileNav
        user={user}
        clientUser={clientUser}
        viewAsId={viewAsId ?? null}
        mainNavItems={mainNavItems}
        secondaryNavItems={secondaryNavItems}
        handleViewAsChange={handleViewAsChange}
        handleLogoutClick={handleLogoutClick}
      />
    </>
  );
}
